import { Controller } from "@hotwired/stimulus";

// Outlet surface used from AgGridComponent (toolkit, read-only here — see H7).
interface AgGridOutlet {
  fetchQuestionData(url: string, scopeId?: string): void;
}

const AG_GRID_ID = "application--toolkit--ag-grid-component--ag-grid-component";

export default class extends Controller {
  static targets = ["questionSelector", "message"];
  static values = {
    questionDataUrl: String,
  };

  declare readonly questionSelectorTarget: HTMLSelectElement;
  declare readonly hasQuestionSelectorTarget: boolean;
  declare readonly messageTarget: HTMLElement;
  declare readonly hasMessageTarget: boolean;
  declare readonly questionDataUrlValue: string;

  connect() {
    void this.refreshMessage();
  }

  switchQuestion() {
    const grid = this.agGrid();
    if (!grid || !this.hasQuestionSelectorTarget) return;
    grid.fetchQuestionData(this.buildQuestionDataUrl());
    void this.refreshMessage();
  }

  // AgGridComponent's fetchQuestionData(url) has no callback (H7 — toolkit untouched), so the
  // preferential-question note / "no votes yet" copy needs its own read of the same JSON to know
  // the row count. Mutually exclusive, mirroring QuestionResultsDetailsComponent: the preferential
  // note only shows alongside rows, "no votes yet" replaces it when there are none.
  private async refreshMessage(): Promise<void> {
    if (!this.hasMessageTarget || !this.hasQuestionSelectorTarget) return;
    const option = this.questionSelectorTarget.selectedOptions[0];
    if (!option) return;

    const preferential = option.dataset.preferentialMessage ?? "";
    const noVotes = option.dataset.noVotesMessage ?? "";

    try {
      const response = await fetch(this.buildQuestionDataUrl(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const json = response.ok ? await response.json() : { rows: [] };
      const hasRows = Array.isArray(json.rows) && json.rows.length > 0;
      const text = hasRows ? preferential : noVotes;
      this.messageTarget.textContent = text;
      this.messageTarget.hidden = text.trim().length === 0;
    } catch (error) {
      console.error("[PublicVoteByVoteGridComponent] message fetch error:", error);
    }
  }

  private agGrid(): AgGridOutlet | null {
    const el = this.element.querySelector(`[data-controller~="${AG_GRID_ID}"]`);
    if (!el) return null;
    return this.application.getControllerForElementAndIdentifier(el, AG_GRID_ID) as unknown as AgGridOutlet | null;
  }

  private buildQuestionDataUrl(): string {
    const params = new URLSearchParams();
    if (this.hasQuestionSelectorTarget) {
      params.set("question_id", this.questionSelectorTarget.value);
    }
    return `${this.questionDataUrlValue}?${params.toString()}`;
  }
}
